// Types
import type { TApiHttpClientRequestConfig } from '~/misc/resource/types';
import type {
  TAutomatedEmailDefaultTemplatesResponse,
  TAutomatedEmailSaleType,
  TAutomatedEmailTemplateActivateRow,
  TAutomatedEmailUserTemplateApi,
  TAutomatedEmailUserTemplateStoreRow,
  TAutomatedEmailUserTriggerApi,
  TAutomatedEmailUserTriggerSyncRow,
} from '~/modules/opportunities/automated-email/types';
import type { TUserTemplateUpdatePayload } from '~/modules/opportunities/automated-email/settings/types';

// Models
import { DefaultResource } from '~/misc/resource/default-resource.model';

// `HttpResourceModel.post` types the body as `Record<string, unknown>`; these endpoints expect a JSON array.
const asPostBody = (data: unknown): Record<string, unknown> => data as Record<string, unknown>;

export class TriggersModel extends DefaultResource {
  protected endpoint: string = 'automated-email/';

  getPatchTriggers(
    patchId: number,
    saleType: TAutomatedEmailSaleType,
    requestConfig?: TApiHttpClientRequestConfig
  ): Promise<TAutomatedEmailUserTriggerApi[]> {
    return this.get<TAutomatedEmailUserTriggerApi[]>(
      'patch-triggers',
      { patch_id: patchId, sale_type: saleType },
      requestConfig
    );
  }

  syncPatchTriggers(
    patchId: number,
    saleType: TAutomatedEmailSaleType,
    triggers: TAutomatedEmailUserTriggerSyncRow[],
    requestConfig?: TApiHttpClientRequestConfig
  ): Promise<TAutomatedEmailUserTriggerApi[]> {
    return this.post<TAutomatedEmailUserTriggerApi[]>(
      'patch-triggers',
      { patch_id: patchId, sale_type: saleType, triggers },
      requestConfig
    );
  }

  getPatchTemplates(
    patchId: number,
    saleType: TAutomatedEmailSaleType,
    requestConfig?: TApiHttpClientRequestConfig
  ): Promise<TAutomatedEmailUserTemplateApi[]> {
    return this.get<TAutomatedEmailUserTemplateApi[]>(
      'patch-templates',
      { patch_id: patchId, sale_type: saleType },
      requestConfig
    );
  }

  getPatchTemplateVariables(
    requestConfig?: TApiHttpClientRequestConfig
  ): Promise<string[]> {
    return this.get<string[]>('patch-templates/variables', undefined, requestConfig);
  }

  getDefaultTemplates(
    saleType: TAutomatedEmailSaleType,
    requestConfig?: TApiHttpClientRequestConfig
  ): Promise<TAutomatedEmailDefaultTemplatesResponse> {
    return this.get<TAutomatedEmailDefaultTemplatesResponse>(
      'default-templates',
      { sale_type: saleType },
      requestConfig
    );
  }

  storePatchTemplates(
    patchId: number,
    saleType: TAutomatedEmailSaleType,
    payload: TAutomatedEmailUserTemplateStoreRow[],
    requestConfig?: TApiHttpClientRequestConfig
  ): Promise<TAutomatedEmailUserTemplateApi[]> {
    return this.post<TAutomatedEmailUserTemplateApi[]>(
      'patch-templates',
      { patch_id: patchId, sale_type: saleType, templates: payload },
      requestConfig
    );
  }

  activatePatchTemplates(
    payload: TAutomatedEmailTemplateActivateRow[],
    requestConfig?: TApiHttpClientRequestConfig
  ): Promise<void> {
    return this.post<void>('patch-templates/activate', asPostBody(payload), requestConfig);
  }

  updatePatchTemplate(
    templateId: number,
    payload: TUserTemplateUpdatePayload,
    requestConfig?: TApiHttpClientRequestConfig
  ): Promise<TAutomatedEmailUserTemplateApi> {
    return this.patch<TAutomatedEmailUserTemplateApi>(
      `patch-templates/${templateId}`,
      payload,
      requestConfig
    );
  }
}
